const KEY = "suedegod.save.v1";

export interface SaveData {
  bestScore: number;
  bestDistance: number;
  totalCoins: number;
  ownedSkins: string[];
  equippedSkin: string;
  missions: {
    dayKey: string;
    daily: { id: string; goal: number; progress: number; reward: number; done: boolean }[];
  };
  lifetime: {
    runs: number;
    totalDistance: number;
    totalCoins: number;
  };
}

const DEFAULT: SaveData = {
  bestScore: 0,
  bestDistance: 0,
  totalCoins: 0,
  ownedSkins: ["default"],
  equippedSkin: "default",
  missions: { dayKey: "", daily: [] },
  lifetime: { runs: 0, totalDistance: 0, totalCoins: 0 },
};

export class Storage {
  private data: SaveData;
  private syncCb: ((d: SaveData) => void) | null = null;

  constructor() {
    this.data = this.load();
  }

  private load(): SaveData {
    try {
      const raw = localStorage.getItem(KEY);
      if (!raw) return structuredClone(DEFAULT);
      const parsed = JSON.parse(raw);
      return { ...structuredClone(DEFAULT), ...parsed };
    } catch {
      return structuredClone(DEFAULT);
    }
  }

  /**
   * Register a callback fired on every save (debounced server sync etc).
   * Not fired by replace() because that's typically a *download* from the
   * authoritative store, not a change we need to push back.
   */
  setSyncCallback(cb: ((d: SaveData) => void) | null): void {
    this.syncCb = cb;
  }

  save(): void {
    try {
      localStorage.setItem(KEY, JSON.stringify(this.data));
    } catch {
      // Quota or private mode — silently ignore.
    }
    if (this.syncCb) this.syncCb(this.data);
  }

  get(): SaveData {
    return this.data;
  }

  update(mut: (d: SaveData) => void): void {
    mut(this.data);
    this.save();
  }

  reset(): void {
    this.data = structuredClone(DEFAULT);
    this.save();
  }

  /** Replace the entire save state from an authoritative source (server). */
  replace(data: Partial<SaveData>): void {
    this.data = { ...structuredClone(DEFAULT), ...data };
    try {
      localStorage.setItem(KEY, JSON.stringify(this.data));
    } catch {
      /* ignore */
    }
  }

  /** Erase the local cache (used when signing out). */
  clearLocal(): void {
    this.data = structuredClone(DEFAULT);
    try {
      localStorage.removeItem(KEY);
    } catch {
      /* ignore */
    }
  }
}
